import type { FC, ReactNode } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import Dimens from 'src/core/utils/Dimens';
import { clamp } from 'src/core/utils/Numbers';
import CarouselPagination, { FIRST_PAGE } from 'src/presentation/components/molecules/CarouselPagination';
import SectionTitle from 'src/presentation/components/molecules/SectionTitle';
import { CAROUSEL_CARD_GAP, useCarouselPager } from 'src/presentation/hooks/useCarouselPager';

interface Props {
	title: string;
	cardWidth: number;
	page: number;
	pageCount: number;
	setPage: (page: number) => void;
	subtitle?: string;
	children: ReactNode;
}

const CarouselSection: FC<Props> = ({ title, cardWidth, page, pageCount, setPage, subtitle, children }) => {
	const pager = useCarouselPager(cardWidth, pageCount, setPage);

	return (
		<View style={styles.section}>
			<View style={styles.header}>
				<SectionTitle style={styles.titleColumn} title={title} {...(subtitle ? { subtitle } : {})} />
				<CarouselPagination page={clamp(page, FIRST_PAGE, pageCount)} pageCount={pageCount} onPageChange={pager.goToPage} />
			</View>
			<ScrollView
				centerContent={false}
				contentContainerStyle={styles.track}
				horizontal={true}
				ref={pager.ref}
				scrollEventThrottle={20}
				showsHorizontalScrollIndicator={false}
				snapToAlignment="start"
				snapToInterval={pager.snapInterval}
				style={styles.trackBleed}
				{...pager.scrollHandlers}
			>
				{children}
			</ScrollView>
		</View>
	);
};

const styles = StyleSheet.create({
	section: {
		paddingHorizontal: Dimens.padding18,
	},
	header: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'flex-end',
		gap: Dimens.gap16,
		marginBottom: Dimens.padding16,
	},
	titleColumn: {
		flex: Dimens.flex1,
	},
	track: {
		paddingHorizontal: Dimens.padding18,
		paddingVertical: Dimens.padding24,
		gap: CAROUSEL_CARD_GAP,
	},
	// The track runs edge to edge so a card can peek past the section padding, and grows vertically
	// so the scroll view does not clip the card shadow.
	trackBleed: {
		marginHorizontal: Dimens.paddingMinus18,
		marginVertical: Dimens.paddingMinus24,
	},
});

export default CarouselSection;
